import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../AuthContext.tsx';
import { Task } from '@/types/tasks.ts';
import { useHouseContext } from '../../HouseContext.tsx';
import { useTaskSync } from '@/hooks/house/tasks/useTaskSync.ts';
import { useTaskCleanup, useTaskPersistence } from '@/hooks/task';
import { safeGetItem, safeSetItem, safeRemoveItem } from '@/utils/storage/storageUtils.ts';

interface TaskContextState {
	tasks: Task[];
	addTask: (
		task: Task,
		completionCallback?: (task: Task) => Promise<void>,
		beforeMarkCompleteCallback?: (task: Task) => Promise<void>,
	) => void;
	addNonPollingTask: (task: Task) => void;
	updateTask: (taskId: string, updates: Partial<Task>) => void;
	removeTask: (id: string) => void;
	clearTasksByType: (taskType: string) => void;
}

const initialState: TaskContextState = {
	tasks: [],
	addTask: () => {},
	addNonPollingTask: () => {},
	updateTask: () => {},
	removeTask: () => {},
	clearTasksByType: () => {},
};

// Create the TaskContext
const TaskContext = createContext<TaskContextState>(initialState);

/**
 * Task Provider Component - Refactored following 7 React Principles
 *
 * PRINCIPLE 1: Declarative - describes what should happen
 * PRINCIPLE 2: Minimal useEffect - only for actual side effects
 * PRINCIPLE 5: Encapsulated logic in custom hooks
 */
export const TaskProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { tokenExpiresAt, token, user } = useAuth();
	const { selectedHouse } = useHouseContext();

	// Derive per-user, per-house storage key
	const storageKey = useMemo(() => {
		const userId = user?.id;
		const houseId = selectedHouse?.id;
		if (!userId) return 'tasks:unknown';
		return `tasks:${userId}:${houseId ?? 'none'}`;
	}, [user?.id, selectedHouse?.id]);

	const [tasks, setTasks] = useState<Task[]>([]);

	const { clearAllTasks, clearTasksByType } = useTaskCleanup(setTasks);

	const { clearTasksFromStorage } = useTaskPersistence(tasks);

	// Load tasks whenever the storage scope changes (user or house)
	useEffect(() => {
		try {
			const saved = safeGetItem(storageKey);
			setTasks(saved ? JSON.parse(saved) : []);
		} catch (err) {
			void err;
			setTasks([]);
		}
	}, [storageKey]);

	// Persist tasks for the current scope
	useEffect(() => {
		try {
			safeSetItem(storageKey, JSON.stringify(tasks));
		} catch (err) {
			void err;
		}
	}, [storageKey, tasks]);

	// Clear tasks when token expires or token is missing (auto-logout safeguard)
	useEffect(() => {
		// Only run cleanup when auth is missing/expired
		if (!tokenExpiresAt || !token) {
			clearAllTasks();
			safeRemoveItem(storageKey);
			clearTasksFromStorage();
		}
	}, [tokenExpiresAt, token, clearAllTasks, clearTasksFromStorage, storageKey]);

	const updateTask = useCallback((taskId: string, updates: Partial<Task>) => {
		setTasks((prevTasks) => {
			return prevTasks.map((task) => {
				if (task.id === taskId) {
					const updatedTask = { ...task, ...updates };
					return updatedTask;
				}
				return task;
			});
		});
	}, []);

	const removeTask = useCallback((id: string) => {
		setTasks((prevTasks) => prevTasks.filter((task) => task.id !== id));
	}, []);

	const { storeCallbacks } = useTaskSync(selectedHouse?.id || null, tasks, updateTask, removeTask);

	const addTask = useCallback(
		(
			task: Task,
			completionCallback?: (task: Task) => Promise<void>,
			beforeMarkCompleteCallback?: (task: Task) => Promise<void>,
		) => {
			const expiresAt = task.expiresAt ?? (tokenExpiresAt !== null ? tokenExpiresAt : undefined);
			const pending = task.pending ?? false;

			const newTask: Task = {
				createdAt: new Date().toISOString(),
				...task,
				expiresAt,
				pending,
				progress: 0,
				isPollingTask: task.isPollingTask ?? true,
			};

			// Update tasks state
			setTasks((prevTasks) => [...prevTasks, newTask]);

			// Store callbacks using useTaskSync's storeCallbacks (FIXED!)
			storeCallbacks(task.id, completionCallback, beforeMarkCompleteCallback);
		},
		[tokenExpiresAt, storeCallbacks],
	);

	const addNonPollingTask = useCallback(
		(task: Task) => {
			const expiresAt = task.expiresAt ?? (tokenExpiresAt !== null ? tokenExpiresAt : undefined);

			const newTask: Task = {
				createdAt: new Date().toISOString(),
				...task,
				expiresAt,
				pending: false,
				progress: 0,
				isPollingTask: false,
			};

			setTasks((prevTasks) => [...prevTasks, newTask]);
		},
		[tokenExpiresAt],
	);

	const contextValue = useMemo(
		() => ({
			tasks,
			addTask,
			addNonPollingTask,
			updateTask,
			removeTask,
			clearTasksByType,
		}),
		[tasks, addTask, addNonPollingTask, updateTask, removeTask, clearTasksByType],
	);

	return <TaskContext.Provider value={contextValue}>{children}</TaskContext.Provider>;
};

// Custom hook for accessing TaskContext
export const useTaskContext = () => useContext(TaskContext);
